import React, { useEffect, useMemo, useState } from "react";
import { adminFetch } from "../adminFetch";

const RANGES = [
    { value: "7", label: "Last 7 days" },
    { value: "30", label: "Last 30 days" },
    { value: "all", label: "All time" },
];

const DIVISIONS = [
    { value: "all", label: "All Divisions" },
    { value: "funding", label: "Funding" },
    { value: "realestate", label: "Real Estate" },
    { value: "materials", label: "Materials" },
    { value: "consulting", label: "Consulting" },
    { value: "contact", label: "Contact" },
];

const computeRange = (rangeVal) => {
    const now = new Date();
    const to = now.toISOString();
    if (rangeVal === "all") {
        return { from: new Date(0).toISOString(), to };
    }
    const days = parseInt(rangeVal, 10);
    const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000).toISOString();
    return { from, to };
};

const formatUSD = (n) =>
    isFinite(n)
        ? new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
              maximumFractionDigits: 2,
          }).format(n)
        : "—";

export const CPLCalculator = () => {
    const [range, setRange] = useState("30");
    const [division, setDivision] = useState("all");
    const [spend, setSpend] = useState("");
    const [leadCount, setLeadCount] = useState(null);
    const [loading, setLoading] = useState(false);

    const { from, to } = useMemo(() => computeRange(range), [range]);

    useEffect(() => {
        let cancelled = false;
        const handle = setTimeout(async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams({ from, to, division });
                const data = await adminFetch(
                    `/admin/stats/range?${params.toString()}`,
                );
                if (!cancelled) {
                    setLeadCount(data.leadCount);
                }
            } catch (e) {
                if (!cancelled) setLeadCount(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, 300);
        return () => {
            cancelled = true;
            clearTimeout(handle);
        };
    }, [from, to, division]);

    const cpl = useMemo(() => {
        const s = parseFloat(spend);
        if (!isFinite(s) || s <= 0 || !leadCount || leadCount === 0) return null;
        return s / leadCount;
    }, [spend, leadCount]);

    return (
        <div
            className="rounded-xl border border-border-subtle bg-elevated p-6"
            data-testid="cpl-calculator"
        >
            <p className="text-[11px] uppercase tracking-widest-2 text-text-muted">
                Cost Per Lead Calculator
            </p>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                    <label className="block text-[11px] font-medium uppercase tracking-widest-2 text-text-muted">
                        Date Range
                    </label>
                    <select
                        value={range}
                        onChange={(e) => setRange(e.target.value)}
                        data-testid="cpl-range"
                        className="mt-2 w-full appearance-none rounded-lg border border-border-subtle bg-[var(--bg-elevated-2)] px-3 py-2.5 text-[14px] text-text-primary focus:outline-none focus:border-[rgba(124,255,79,0.55)]"
                    >
                        {RANGES.map((r) => (
                            <option key={r.value} value={r.value}>
                                {r.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-[11px] font-medium uppercase tracking-widest-2 text-text-muted">
                        Division
                    </label>
                    <select
                        value={division}
                        onChange={(e) => setDivision(e.target.value)}
                        data-testid="cpl-division"
                        className="mt-2 w-full appearance-none rounded-lg border border-border-subtle bg-[var(--bg-elevated-2)] px-3 py-2.5 text-[14px] text-text-primary focus:outline-none focus:border-[rgba(124,255,79,0.55)]"
                    >
                        {DIVISIONS.map((d) => (
                            <option key={d.value} value={d.value}>
                                {d.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-[11px] font-medium uppercase tracking-widest-2 text-text-muted">
                        Ad Spend (USD)
                    </label>
                    <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={spend}
                        onChange={(e) => setSpend(e.target.value)}
                        placeholder="0.00"
                        data-testid="cpl-spend"
                        className="mt-2 w-full rounded-lg border border-border-subtle bg-[var(--bg-elevated-2)] px-3 py-2.5 text-[14px] text-text-primary placeholder:text-text-muted focus:outline-none focus:border-[rgba(124,255,79,0.55)]"
                    />
                </div>
            </div>
            <div
                className="mt-5 flex flex-wrap items-baseline gap-x-6 gap-y-2 border-t border-border-subtle pt-5"
                data-testid="cpl-output"
            >
                <p className="text-[14px] text-text-secondary">
                    Leads in window:{" "}
                    <span className="font-medium text-text-primary tabular-nums">
                        {loading ? "…" : leadCount ?? "—"}
                    </span>
                </p>
                <p className="text-[14px] text-text-secondary">
                    Cost per Lead:{" "}
                    <span
                        className="font-display text-[18px] font-semibold tabular-nums"
                        style={{ color: "#E8FFD8" }}
                    >
                        {cpl ? formatUSD(cpl) : "—"}
                    </span>
                </p>
            </div>
        </div>
    );
};

export default CPLCalculator;
